import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    ServiceRequestFormData,
    OrganizationData,
    Package,
    Service,
    Discount
} from '../../types';
import { filterAvailablePackages } from '../../utils/packageFilters';
import { canAccessFeature } from '../../utils/tierPermissions';
import Slider from 'react-slick';
import './StepStyles.scss';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

interface Step6PackageProps {
  formData: ServiceRequestFormData;
  updateFormData: (data: Partial<ServiceRequestFormData>) => void;
  registerValidation: (validator: () => boolean) => void;
  organizationData: OrganizationData;
}

interface PackageWithServices extends Package {
  includedServices: Service[];
  discountedPrice?: number;
  appliedDiscount?: Discount;
}

interface PackageCardProps {
    pkg: PackageWithServices;
    isSelected: boolean;
    onSelect: (packageId: number) => void;
    includedServices: Service[];
}

const PackageCard: React.FC<PackageCardProps> = ({ pkg, isSelected, onSelect, includedServices }) => {
    const EPSILON = 0.001;
    const hasDiscount = pkg.discountedPrice !== undefined && 
      Math.abs((pkg.discountedPrice - (pkg.price || 0))) > EPSILON;
    const formattedOriginalPrice = pkg.price !== null ? `$${Number(pkg.price).toFixed(2)}` : 'N/A';
    const formattedDiscountedPrice = pkg.discountedPrice !== undefined ? 
      `$${Number(pkg.discountedPrice).toFixed(2)}` : null;

    return (
        <div className={`package-card has-shadow ${isSelected ? 'selected' : ''}`}>
            <div>
                <h5 className="package-name">{pkg.name}</h5>
                <div className="package-price">
                    {hasDiscount ? (
                        <>
                            <span className="original-price">{formattedOriginalPrice}</span>
                            <span className="discounted-price">{formattedDiscountedPrice}</span>
                            {pkg.appliedDiscount && (
                                <div className="discount-badge">
                                    {pkg.appliedDiscount.discount_type === 'percentage' 
                                        ? `${pkg.appliedDiscount.amount}% off` 
                                        : `$${pkg.appliedDiscount.amount} off`}
                                </div>
                            )}
                        </>
                    ) : (
                        formattedOriginalPrice
                    )}
                </div>
                <p className="package-description">{pkg.description}</p>

                {includedServices.length > 0 && (
                    <div className="included-services">
                        <h6>Included Services:</h6>
                        <ul>
                            {includedServices.map((service) => (
                                <li key={service.id}>
                                    <span className="service-name">{service.name}</span>
                                    {service.description && (
                                        <p className="service-description">{service.description}</p>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <button
                className={`btn ${isSelected ? 'btn-success' : 'btn-primary'} btn-select-package`}
                onClick={() => pkg.id && onSelect(pkg.id)}
                disabled={!pkg.id}
            >
                {isSelected ? 'Selected' : 'Select Package'}
            </button>
        </div>
    );
};

function Step6Package({
  formData,
  updateFormData,
  registerValidation,
  organizationData,
}: Step6PackageProps) {
  const [validationError, setValidationError] = useState<string | null>(null);

  // Memoized map for package_id -> [service_id, ...]
  const packageServiceMap = useMemo(() => {
      const map = new Map<number, number[]>();
      (organizationData?.packageServiceLinks || []).forEach(link => {
          if (!map.has(link.package_id)) { map.set(link.package_id, []); }
          map.get(link.package_id)?.push(link.service_id);
      });
      return map;
  }, [organizationData?.packageServiceLinks]);

  const serviceMapById = useMemo(() => {
      const map = new Map<number, Service>();
      (organizationData?.allServices || []).forEach(service => {
          map.set(service.id, service);
      });
      return map;
  }, [organizationData?.allServices]);

  // Create a map of packages to their applicable discounts
  const packageDiscountMap = useMemo(() => {
    const map = new Map<number, Discount[]>();
    
    if (!organizationData?.discounts || !organizationData?.packageDiscounts) {
      return map;
    }
    
    // Group discounts by package ID
    organizationData.packageDiscounts.forEach(link => {
      const discount = organizationData.discounts.find(d => d.id === link.discount_id);
      
      if (discount) {
        if (!map.has(link.package_id)) {
          map.set(link.package_id, []);
        }
        map.get(link.package_id)?.push(discount);
      }
    });
    
    return map;
  }, [organizationData?.discounts, organizationData?.packageDiscounts]);
  
  // Function to calculate the best discount for a package
  const getBestDiscount = useCallback((packagePrice: number, discounts: Discount[] | undefined): {
    discountedPrice: number;
    appliedDiscount: Discount;
  } | undefined => {
    if (!discounts || discounts.length === 0) {
      return undefined;
    }
    
    // Ensure packagePrice is a number
    const price = typeof packagePrice === 'string' ? parseFloat(packagePrice) : packagePrice;
    
    // Calculate all possible discounted prices
    const discountCalculations = discounts.map(discount => {
      let discountedPrice = price;
      
      if (discount.discount_type === 'percentage') {
        discountedPrice = price * (1 - discount.amount / 100);
      } else { // fixed amount
        discountedPrice = Math.max(0, price - discount.amount);
      }
      
      return {
        discountedPrice: parseFloat(discountedPrice.toFixed(2)),
        appliedDiscount: discount
      };
    });
    
    // Return the discount that gives the lowest price
    return discountCalculations.reduce((best, current) => 
      current.discountedPrice < best.discountedPrice ? current : best
    );
  }, []);

  // Filter Packages and add service objects
  const filteredPackagesWithServices: PackageWithServices[] = useMemo(() => {
    // Use the shared utility function to get matching package IDs
    const matchingPackageIds = filterAvailablePackages(organizationData, formData);

    if (matchingPackageIds.length === 0) {
      return [];
    }

    const packages = organizationData?.packages || [];

    // Enrich matching packages with services and discount information
    return matchingPackageIds
      .map(pkgId => packages.find(p => p.id === pkgId))
      .filter((pkg): pkg is Package => pkg !== undefined)
      .map(pkg => {
        const serviceIds = packageServiceMap.get(pkg.id!) || [];
        const includedServices = serviceIds
          .map(id => serviceMapById.get(id))
          .filter((service): service is Service => !!service);

        // Get applicable discounts and calculate best discount
        const discounts = packageDiscountMap.get(pkg.id!);
        const bestDiscount = pkg.price ? getBestDiscount(pkg.price, discounts) : undefined;

        return {
          ...pkg,
          includedServices,
          ...(bestDiscount ? {
            discountedPrice: bestDiscount.discountedPrice,
            appliedDiscount: bestDiscount.appliedDiscount
          } : {})
        };
      });
  }, [
    formData,
    organizationData,
    packageServiceMap,
    serviceMapById,
    packageDiscountMap,
    getBestDiscount
  ]);

  // Handler for selecting/deselecting a package
  const handlePackageSelect = (packageId: number) => {
    updateFormData({ selectedPackageId: packageId === formData.selectedPackageId ? null : packageId });
    setValidationError(null);
  };

  const validateStep = useCallback(() => {
    const isRepairStatus = formData.selectedStatus === 'repair' || formData.selectedStatus === 'repair_maintenance';
    const packageOptional = canAccessFeature(organizationData.organization.tier, 'optionalRepairPackage') && isRepairStatus;

    if (filteredPackagesWithServices.length > 0 && !formData.selectedPackageId && !packageOptional) {
      setValidationError("Please select a service package.");
      return false;
    }
    setValidationError(null);
    return true;
  }, [formData.selectedPackageId, formData.selectedStatus, filteredPackagesWithServices.length, organizationData.organization.tier]);

  // Register validation effect
  useEffect(() => {
    registerValidation(validateStep);
  }, [registerValidation, validateStep]);

  // Memoized slider settings
  const sliderSettings = useMemo(() => ({
    dots: true,
    infinite: filteredPackagesWithServices.length > 3,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: Math.min(filteredPackagesWithServices.length, 2),
          infinite: filteredPackagesWithServices.length > 2,
          dots: true,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          infinite: filteredPackagesWithServices.length > 1,
        }
      }
    ]
  }), [filteredPackagesWithServices.length]);

  return (
    <div className="form-step step-package">
      <h2>Select Service Package</h2>
      <p className="step-intro">
        {(() => {
          const isRepairStatus = formData.selectedStatus === 'repair' || formData.selectedStatus === 'repair_maintenance';
          const isOptional = canAccessFeature(organizationData.organization.tier, 'optionalRepairPackage') && isRepairStatus;

          if (isOptional) {
            return formData.requestLocation
              ? `Based on your selections for a ${formData.selectedStatus} service at ${formData.requestLocation === 'home' ? 'your location' : 'the shop'}, you may choose an optional package or skip to continue:`
              : `Based on your previous selections, you may choose an optional package or skip to continue:`;
          }

          return formData.requestLocation
            ? `Based on your selections for a ${formData.selectedStatus} service at ${formData.requestLocation === 'home' ? 'your location' : 'the shop'}, choose a package:`
            : `Based on your previous selections, choose the package that best fits your needs.`;
        })()}
      </p>

      {validationError && (<div className="error-message summary-error">{validationError}</div>)}

      <div className="package-carousel-container">
        {filteredPackagesWithServices.length === 0 ? (
          <div className="empty-state info-message">
            {!formData.selectedCategoryId || !formData.selectedMakeId || !formData.selectedStatus
              ? <p>Please complete previous steps (Machine, Service Reason) to see available packages.</p>
              : !formData.requestLocation && organizationData?.organizationStatusSettings?.find(s =>
                  s.status_value === formData.selectedStatus)?.service_location === 'both'
                ? <p>Please select your preferred service location in the previous step to see packages.</p>
                : <p>No packages match your specific combination of machine, service reason
                    {formData.requestLocation ? `, and service location (${formData.requestLocation === 'home' ? 'at home' : 'at shop'})` : ''}.</p>
            }
          </div>
        ) : filteredPackagesWithServices.length <= 2 ? (
          // Simple grid for 1-2 packages (no carousel needed)
          <div className="package-grid">
            {filteredPackagesWithServices.map((pkg) => (
              <div key={pkg.id} className="package-grid-item">
                <PackageCard
                  pkg={pkg}
                  isSelected={formData.selectedPackageId === pkg.id}
                  onSelect={handlePackageSelect}
                  includedServices={pkg.includedServices}
                />
              </div>
            ))}
          </div>
        ) : (
          // Carousel for 3+ packages
          <Slider {...sliderSettings}>
            {filteredPackagesWithServices.map((pkg) => (
              <div key={pkg.id} className="package-slide">
                <PackageCard
                  pkg={pkg}
                  isSelected={formData.selectedPackageId === pkg.id}
                  onSelect={handlePackageSelect}
                  includedServices={pkg.includedServices}
                />
              </div>
            ))}
          </Slider>
        )}
      </div>
    </div>
  );
}

export default Step6Package;